// GESTOR DE SESSÃO AGORA + FIREBASE
//
// Um sítio só para entrar e sair de um canal, partilhado pelos quatro ecrãs de
// jogo. Resolve quatro problemas que existiam em cada ficheiro por sua conta:
//
//   1. Ninguém desligava nada ao sair. O cliente era um singleton criado no
//      import, os handlers "user-published" acumulavam-se a cada revanche e as
//      câmaras ficavam acesas depois de sair do estádio.
//   2. Dois joins podiam correr ao mesmo tempo (StrictMode monta duas vezes,
//      e a revanche disparava outro) e ficavam a lutar pelo mesmo canal.
//   3. Se a rede morresse a meio, o ecrã ficava eternamente em "A ENTRAR NO
//      ESTÁDIO..." sem nunca dizer porquê.
//   4. Uma câmara negada ou ocupada por outra app não dava erro nenhum —
//      simplesmente não acontecia nada.
//
// Regra de ouro: NUNCA entrar sem antes ter terminado a sessão anterior. Todas
// as operações passam por uma fila (ver `encadear`), por isso nunca há um
// entrar() e um terminar() a correr em simultâneo.

export const TEMPO_LIMITE_ENTRADA_MS = 15000;

export const ERRO = {
  TIMEOUT: 'TIMEOUT',
  PERMISSAO: 'PERMISSAO',
  OCUPADO: 'OCUPADO',
  SEM_DISPOSITIVO: 'SEM_DISPOSITIVO',
  LIGACAO: 'LIGACAO',
};

const MENSAGENS = {
  [ERRO.TIMEOUT]: 'O estádio demorou demasiado a abrir. Verifica a tua ligação e tenta de novo.',
  [ERRO.PERMISSAO]: 'Precisamos da câmara e do microfone para entrares em campo. Autoriza o acesso nas definições do navegador e tenta de novo.',
  [ERRO.OCUPADO]: 'A câmara ou o microfone estão a ser usados por outra aplicação. Fecha-a e tenta de novo.',
  [ERRO.SEM_DISPOSITIVO]: 'Não encontrámos câmara ou microfone neste dispositivo.',
  [ERRO.LIGACAO]: 'Não foi possível ligar ao estádio. Verifica a internet e tenta de novo.',
};

export function mensagemDoErro(codigo) {
  return MENSAGENS[codigo] || MENSAGENS[ERRO.LIGACAO];
}

// Traduz um erro do getUserMedia OU do AgoraRTC num código nosso.
// O navegador usa err.name ("NotAllowedError"); o AgoraRTC usa err.code
// ("PERMISSION_DENIED"). Olhamos para os dois, e ainda para a mensagem, porque
// as versões do SDK trocam de nomenclatura entre si.
export function classificarErro(erro) {
  if (!erro) return ERRO.LIGACAO;
  if (erro.codigoSessao) return erro.codigoSessao;

  const nome = String(erro.name || '');
  const codigo = String(erro.code || '');
  const texto = `${nome} ${codigo} ${erro.message || ''}`.toUpperCase();

  if (nome === 'TimeoutError' || codigo === ERRO.TIMEOUT) return ERRO.TIMEOUT;

  if (nome === 'NotAllowedError' || nome === 'SecurityError'
    || texto.includes('PERMISSION_DENIED') || texto.includes('NOT_ALLOWED')) {
    return ERRO.PERMISSAO;
  }

  if (nome === 'NotReadableError' || nome === 'AbortError'
    || texto.includes('NOT_READABLE') || texto.includes('TRACK_START_FAILED')
    || texto.includes('DEVICE_IN_USE')) {
    return ERRO.OCUPADO;
  }

  if (nome === 'NotFoundError' || nome === 'OverconstrainedError'
    || texto.includes('DEVICE_NOT_FOUND') || texto.includes('NOT_SUPPORTED')) {
    return ERRO.SEM_DISPOSITIVO;
  }

  return ERRO.LIGACAO;
}

// O bundle do AgoraRTC é pesado e só serve dentro de uma partida, por isso é
// carregado à primeira entrada em vez de no arranque da app. Como bónus, os
// testes conseguem correr em Node puro: sem esta importação preguiçosa, o
// bundle (que assume um navegador) rebentava só por importar este ficheiro.
let sdkPadrao = null;
async function obterSdkPadrao() {
  if (!sdkPadrao) {
    const modulo = await import('agora-rtc-sdk-ng');
    sdkPadrao = modulo.default || modulo;
  }
  return sdkPadrao;
}

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ============================================================================
// VÍDEO DENTRO DA JANELA
// ============================================================================
//
// O que aparece no ecrã é um <video> NOSSO, alimentado diretamente pela pista
// (`track.getMediaStreamTrack()`), e não a árvore que o `track.play()` do
// AgoraRTC monta. Porquê:
//
//   1. A árvore do SDK era a única coisa no ecrã que não controlávamos: ele
//      escreve os estilos dele, reescreve-os quando lhe apetece, e no Android
//      ainda mete um `poster="noposter"` (que aqui vai bater no index.html).
//      Já se tentou domá-la por CSS e por estilos no elemento; com um elemento
//      nosso não há nada para domar.
//   2. O `play()` do SDK ACRESCENTA ao contentor em vez de substituir. Numa
//      revanche ficavam dois vídeos empilhados; a limpeza por `innerHTML = ''`
//         resolvia isso mas levava à frente a barra de BOLAS, que o React
//      também tinha lá dentro (e o React não sabe que ela desapareceu).
//
// Por isso cada janela de PK tem um filho próprio para a câmara:
//
//   <div class="janela-video">        ← foto de fundo, cantos redondos
//     <div class="ecra-camara" />     ← SÓ o vídeo entra aqui (é o que limpamos)
//       <div>BOLAS...</div>         ← filhos do React, ficam intactos
//   </div>
//
// Onde não houver `.ecra-camara` (LiveCampo, testes), a janela é o próprio
// ecrã — comportamento antigo. Se a pista não tiver getMediaStreamTrack (SDK
// muito antigo, pista falsa dos testes), cai-se no `play()` do SDK com a
// árvore ajustada (ver ajustarArvoreDoVideo).

export const CLASSE_ECRA = 'ecra-camara';
export const CLASSE_VIDEO = 'video-camara';

// Estilos escritos NO ELEMENTO com prioridade: não dependem de o CSS ter
// carregado, nem da especificidade, nem de o browser conhecer o selector.
function escreverComPrioridade(estilo, pares) {
  if (!estilo || typeof estilo.setProperty !== 'function') return;
  Object.entries(pares).forEach(([nome, valor]) => estilo.setProperty(nome, valor, 'important'));
}

// Caixa presa pelos quatro lados, sem percentagens para resolver.
const CAIXA_INTEIRA = { position: 'absolute', top: '0', left: '0', right: '0', bottom: '0', width: 'auto', height: 'auto' };

// Ajusta a árvore que o AgoraRTC monta (caminho de recurso):
//   <div id="agora-video-player-…" style="width:100%;height:100%;position:relative">
//     <video style="width:100%;height:100%;position:absolute;top:0;left:0">
// O wrapper fica preso pelos quatro lados e o <video> a 100% com cover.
export function ajustarArvoreDoVideo(elemento) {
  if (!elemento) return false;

  const wrapper = elemento.firstElementChild;
  if (wrapper && wrapper.style) escreverComPrioridade(wrapper.style, CAIXA_INTEIRA);

  const video = typeof elemento.querySelector === 'function'
    ? elemento.querySelector('video')
    : null;
  if (video && video.style) {
    escreverComPrioridade(video.style, { width: '100%', height: '100%', 'object-fit': 'cover' });
  }

  return !!video;
}

// O <video> nem sempre está montado no mesmo instante do play(), e o SDK
// reescreve os estilos quando a pista muda de resolução. Por isso insistimos
// algumas vezes, em vez de aplicar uma só vez e esperar pelo melhor.
function insistirNoAjuste(elemento) {
  if (ajustarArvoreDoVideo(elemento)) return;
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => ajustarArvoreDoVideo(elemento));
  }
  [120, 400, 1200].forEach((ms) => setTimeout(() => ajustarArvoreDoVideo(elemento), ms));
}

// Onde é que o vídeo vai parar dentro da janela: o filho `.ecra-camara` se
// existir, senão a própria janela.
export function ecraDaJanela(elemento) {
  if (!elemento) return null;
  const filhos = elemento.children ? Array.from(elemento.children) : [];
  const ecra = filhos.find((f) => f && f.classList && f.classList.contains(CLASSE_ECRA));
  return ecra || elemento;
}

// Esvazia SÓ o ecrã da câmara. A barra de BOLAS e o resto ficam.
export function limparJanelaDeVideo(elemento) {
  const ecra = ecraDaJanela(elemento);
  if (!ecra) return false;
  ecra.innerHTML = '';
  return true;
}

// As pistas remotas têm getUserId(); as locais não.
function ehPistaLocal(track) {
  return !!track && typeof track.getUserId !== 'function';
}

// O iOS 15 tem um bug em que um <video> com transform deixa de desenhar.
// O próprio SDK não espelha nesse caso; fazemos o mesmo.
function ehIos15() {
  if (typeof navigator === 'undefined') return false;
  return /\bOS 15_\d/.test(String(navigator.userAgent || ''));
}

// Constrói o nosso <video> para uma pista. Devolve null se a pista não der
// acesso ao MediaStreamTrack (aí usa-se o play() do SDK).
// `doc` e `Stream` são injetáveis para os testes correrem em Node.
export function construirVideoProprio(track, { fit = 'cover', doc = globalThis.document, Stream = globalThis.MediaStream } = {}) {
  if (!track || typeof track.getMediaStreamTrack !== 'function' || !doc || !Stream) return null;
  const pista = track.getMediaStreamTrack();
  if (!pista) return null;

  const video = doc.createElement('video');
  video.className = CLASSE_VIDEO;
  // Autoplay silencioso e em linha: é o que todos os navegadores deixam
  // arrancar sem toque do utilizador.
  video.muted = true;
  video.autoplay = true;
  video.playsInline = true;
  video.controls = false;
  video.disablePictureInPicture = true;
  video.setAttribute('muted', '');
  video.setAttribute('autoplay', '');
  video.setAttribute('playsinline', '');

  const local = ehPistaLocal(track);
  escreverComPrioridade(video.style, {
    position: 'absolute', top: '0', left: '0', width: '100%', height: '100%',
    'object-fit': fit,
    // A própria câmara vê-se ao espelho, como o SDK fazia.
    transform: local && !ehIos15() ? 'scaleX(-1)' : 'none',
  });

  video.srcObject = new Stream([pista]);

  // Se o SDK trocar a pista por baixo (mudança de câmara), acompanhamos.
  if (typeof track.on === 'function') {
    try {
      track.on('track-updated', () => {
        const nova = track.getMediaStreamTrack();
        if (nova) video.srcObject = new Stream([nova]);
      });
    } catch { /* o SDK não conhece o evento; não faz mal */ }
  }

  return video;
}

// Arranca o vídeo. Se o navegador travar o autoplay, volta a tentar ao
// primeiro toque e quando o separador voltar a estar visível.
function arrancarVideo(video) {
  const tentar = () => {
    try {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch { /* ignora */ }
  };
  tentar();
  if (typeof document === 'undefined') return;

  const outraVez = () => {
    if (!video.isConnected || !video.paused) {
      document.removeEventListener('visibilitychange', outraVez);
      return;
    }
    if (document.visibilityState === 'visible') tentar();
  };
  document.addEventListener('visibilitychange', outraVez);
  document.addEventListener('touchstart', () => { if (video.paused) tentar(); }, { once: true, passive: true });
  document.addEventListener('click', () => { if (video.paused) tentar(); }, { once: true });
}

// Mede o que ficou no ecrã e diz na consola. É isto que distingue "o vídeo
// não enche a janela" (problema nosso) de "a imagem que chega é assim"
// (câmara/cenário). Também está em gfl.janelas().
export function medirJanela(janela, video) {
  if (!janela || !video || typeof janela.getBoundingClientRect !== 'function') return null;
  const j = janela.getBoundingClientRect();
  const v = video.getBoundingClientRect();
  return {
    janela: `${Math.round(j.width)}×${Math.round(j.height)}`,
    video: `${Math.round(v.width)}×${Math.round(v.height)}`,
    pista: `${video.videoWidth || 0}×${video.videoHeight || 0}`,
    enche: Math.abs(v.height - j.height) <= 2 && Math.abs(v.width - j.width) <= 2,
  };
}

function relatarNaConsola(janela, video) {
  if (typeof video.addEventListener !== 'function') return;
  let relatado = false;
  const relatar = () => {
    setTimeout(() => {
      // Só uma vez, e só se este <video> ainda for o que está na janela (uma
      // revanche substitui-o antes de ele chegar a medir-se).
      if (relatado || video.isConnected === false) return;
      const m = medirJanela(janela, video);
      if (!m) return;
      relatado = true;
      const texto = `[câmara] ${janela.id || 'janela'}: janela ${m.janela} · vídeo ${m.video} · pista ${m.pista}`;
      if (m.enche) console.log(texto);
      else console.warn(`${texto} — O VÍDEO NÃO ENCHE A JANELA`);
    }, 500);
  };
  video.addEventListener('loadedmetadata', relatar, { once: true });
  video.addEventListener('resize', relatar, { once: true });
}

// Coloca uma pista de vídeo dentro de uma janela.
export function reproduzirVideoNoContentor(track, contentor, { fit = 'cover' } = {}) {
  if (!track || !contentor) return false;

  const elemento = typeof contentor === 'string'
    ? (typeof document !== 'undefined' ? document.getElementById(contentor) : null)
    : contentor;
  if (!elemento) return false;

  const ecra = ecraDaJanela(elemento);

  try {
    ecra.innerHTML = '';               // fora o que lá estivesse (só no ecrã da câmara)

    const video = construirVideoProprio(track, { fit });
    if (video) {
      // O ecrã da câmara fica preso pelos quatro lados da janela, mesmo que o
      // CSS ainda não tenha chegado.
      if (ecra !== elemento) escreverComPrioridade(ecra.style, CAIXA_INTEIRA);
      ecra.appendChild(video);
      arrancarVideo(video);
      relatarNaConsola(elemento, video);
      return true;
    }

    // Recurso: play() do SDK com a árvore dele ajustada.
    track.play(ecra, { fit });
    insistirNoAjuste(ecra);
    return true;
  } catch (erro) {
    console.warn('Não foi possível mostrar o vídeo:', erro);
    return false;
  }
}

// Espera que a janela exista no DOM antes de lá pôr o vídeo. O React só desenha
// a <div id="janela-local"> depois deste código correr, por isso tentamos
// durante ~2s em vez de adivinhar um setTimeout fixo.
async function reproduzirQuandoExistir(track, contentor, foiCancelado) {
  if (!track || !contentor) return false;
  if (typeof contentor !== 'string') return reproduzirVideoNoContentor(track, contentor);
  if (typeof document === 'undefined') return false;

  for (let tentativa = 0; tentativa < 40; tentativa++) {
    if (foiCancelado()) return false;
    if (document.getElementById(contentor)) {
      return reproduzirVideoNoContentor(track, contentor);
    }
    await esperar(50);
  }
  return false;
}

// Registo global de todas as sessões vivas. Serve para o lobby poder garantir
// que não sobrou nenhuma câmara acesa, e para os testes de verificação.
const SESSOES_VIVAS = new Set();

export function sessoesAtivas() {
  return [...SESSOES_VIVAS].map((s) => s.diagnostico());
}

export async function terminarTodasAsSessoes() {
  await Promise.all([...SESSOES_VIVAS].map((s) => s.terminar()));
}

// O `sdk` é injetável só para os testes conseguirem correr esta lógica sem um
// navegador nem hardware de câmara. Em produção fica sempre o AgoraRTC real.
export function criarSessaoAgora(etiqueta = 'sessao', { sdk: sdkInjetado = null } = {}) {
  // Cada tentativa de entrada leva um número. Quando alguém chama terminar()
  // (ou um novo entrar()), o número muda e a tentativa antiga percebe, na
  // linha a seguir ao próximo await, que já não é a dona da sessão e desiste.
  // É isto que impede um join lançado antes do unmount de acabar por publicar
  // uma câmara num ecrã que já não existe.
  let geracao = 0;
  let fila = Promise.resolve();
  let pedidoEmCurso = false;

  const sessao = {
    etiqueta,
    cliente: null,
    audio: null,
    video: null,
    aEntrar: false,
    unsubs: [],
    contentorLocal: null,
  };

  const foiCancelado = (minhaGeracao) => minhaGeracao !== geracao;

  const abortar = () => {
    const e = new Error('Sessão abortada');
    e.abortado = true;
    return e;
  };

  // Serializa tudo: entrar() e terminar() nunca correm ao mesmo tempo, mesmo
  // que o React os dispare no mesmo tick.
  const encadear = (operacao) => {
    const resultado = fila.then(operacao, operacao);
    fila = resultado.then(() => {}, () => {});
    return resultado;
  };

  // ---------------------------------------------------------------- LISTENERS
  // Todo o onSnapshot desta sala é registado aqui para que o terminar() possa
  // desligá-los a todos de uma vez, sem depender de cada useEffect.
  sessao.registarListener = (unsub) => {
    if (typeof unsub === 'function') sessao.unsubs.push(unsub);
    return unsub;
  };

  sessao.desligarListeners = () => {
    const lista = sessao.unsubs;
    sessao.unsubs = [];
    lista.forEach((unsub) => {
      try { unsub(); } catch (e) { console.warn(`[${etiqueta}] listener não desligou:`, e); }
    });
    return lista.length;
  };

  // ---------------------------------------------------------------- TEARDOWN
  // A ordem importa: despublicar antes de fechar as pistas (senão o canal fica
  // com pistas mortas publicadas), remover os handlers antes do leave (senão o
  // leave dispara user-unpublished para dentro de código já desmontado), e só
  // no fim largar a referência do cliente.
  // Só o lado do Agora. É isto que corre antes de cada nova entrada: limpar o
  // canal anterior NÃO pode arrastar consigo os onSnapshot do ecrã, senão uma
  // revanche deixava a sala sem placar, sem chat e sem lista de adeptos.
  const desligarAgora = async () => {
    geracao += 1;
    sessao.aEntrar = false;

    const cliente = sessao.cliente;
    const pistas = [sessao.audio, sessao.video].filter(Boolean);

    // 1. Despublicar
    if (cliente && pistas.length) {
      try { await cliente.unpublish(pistas); } catch { /* já estava fora do canal */ }
    }

    // 2. Parar e fechar as pistas locais (é isto que apaga a luz da câmara)
    pistas.forEach((pista) => {
      try { pista.stop(); } catch { /* ignora */ }
      try { pista.close(); } catch { /* ignora */ }
    });
    sessao.audio = null;
    sessao.video = null;

    // 2b. A janela local fica sem imagem: com a câmara fechada, ficava lá o
    //     último fotograma congelado.
    if (typeof document !== 'undefined' && typeof sessao.contentorLocal === 'string') {
      limparJanelaDeVideo(document.getElementById(sessao.contentorLocal));
    }

    // 3. Remover TODOS os handlers do Agora
    if (cliente) {
      try { cliente.removeAllListeners(); } catch { /* ignora */ }
    }

    // 4. Sair do canal
    if (cliente) {
      try { await cliente.leave(); } catch { /* já podia estar fora */ }
    }

    // 5. Largar a referência ao cliente
    sessao.cliente = null;

    SESSOES_VIVAS.delete(sessao);
  };

  // Teardown completo: o Agora MAIS os listeners do Firebase. Corre quando se
  // sai mesmo do ecrã, nunca entre duas partidas da mesma sala.
  const terminarInterno = async () => {
    await desligarAgora();
    sessao.desligarListeners();
  };

  sessao.terminar = () => {
    // Sinaliza a cancelação de imediato: o join que estiver a decorrer vê isto
    // no seu próximo ponto de controlo e desiste em vez de continuar a publicar
    // câmaras para um ecrã que já foi desmontado.
    geracao += 1;
    sessao.aEntrar = false;
    // Liberta a tranca para que uma nova entrada (revanche, reentrada) possa
    // ser pedida assim que este teardown termine.
    pedidoEmCurso = false;
    return encadear(terminarInterno);
  };

  // ---------------------------------------------------------------- ENTRADA
  const entrarInterno = async ({
    appId,
    canal,
    token = null,
    uid = null,
    modo = 'rtc',
    codec = 'vp8',
    papel = null,
    publicar = false,
    opcoesAudio = { AEC: true, ANS: true },
    opcoesVideo = { encoderConfig: '480p_1' },
    contentorLocal = null,
    aoPublicarRemoto = null,
    aoDespublicarRemoto = null,
    aoMudarEstadoLigacao = null,
    tempoLimiteMs = TEMPO_LIMITE_ENTRADA_MS,
  }) => {
    if (!appId || !canal) {
      return { ok: false, codigo: ERRO.LIGACAO, erro: new Error('appId ou canal em falta') };
    }

    // Rede de segurança: a fila já garante que não há duas entradas ao mesmo
    // tempo, por isso chegar aqui com aEntrar a true seria um bug nosso.
    if (sessao.aEntrar) {
      console.warn(`[${etiqueta}] entrada concorrente detetada — ignorada`);
      return { ok: false, ocupado: true };
    }

    // Nunca sobrepor sessões: fecha o canal anterior antes de abrir o novo.
    try { await desligarAgora(); } catch (e) { console.warn(`[${etiqueta}] limpeza prévia falhou:`, e); }

    sessao.aEntrar = true;
    const minhaGeracao = geracao;
    const cancelado = () => foiCancelado(minhaGeracao);

    const sequencia = async () => {
      const sdk = sdkInjetado || await obterSdkPadrao();
      if (cancelado()) throw abortar();

      // Cliente novo em cada entrada — o antigo foi anulado no teardown.
      const cliente = sdk.createClient({ mode: modo, codec });
      if (cancelado()) { throw abortar(); }
      sessao.cliente = cliente;
      SESSOES_VIVAS.add(sessao);

      // Handlers ANTES do join: o Agora dispara user-published para quem já lá
      // estava assim que entramos, e se só ligássemos os handlers depois
      // perdíamos o vídeo de quem chegou primeiro.
      if (aoPublicarRemoto) {
        cliente.on('user-published', async (utilizador, tipoMedia) => {
          if (cancelado()) return;
          try {
            await cliente.subscribe(utilizador, tipoMedia);
            if (cancelado()) return;
            aoPublicarRemoto(utilizador, tipoMedia, cliente);
          } catch (e) {
            console.warn(`[${etiqueta}] falhou a subscrição de ${utilizador?.uid}:`, e);
          }
        });
      }
      if (aoDespublicarRemoto) {
        cliente.on('user-unpublished', (utilizador, tipoMedia) => {
          if (cancelado()) return;
          aoDespublicarRemoto(utilizador, tipoMedia, cliente);
        });
      }
      if (aoMudarEstadoLigacao) {
        cliente.on('connection-state-change', (atual, anterior, razao) => {
          if (cancelado()) return;
          aoMudarEstadoLigacao(atual, anterior, razao);
        });
      }

      if (papel) {
        try { await cliente.setClientRole(papel); } catch { /* já era esse papel */ }
        if (cancelado()) throw abortar();
      }

      await cliente.join(appId, canal, token, uid);
      if (cancelado()) throw abortar();

      if (publicar) {
        // Cada pista é guardada na sessão MAL é criada. Se o utilizador sair
        // entre a criação do microfone e a da câmara, o teardown já encontra o
        // microfone e fecha-o — senão ficava um microfone órfão ligado.
        sessao.audio = await sdk.createMicrophoneAudioTrack(opcoesAudio);
        if (cancelado()) throw abortar();

        sessao.video = await sdk.createCameraVideoTrack(opcoesVideo);
        if (cancelado()) throw abortar();

        await cliente.publish([sessao.audio, sessao.video]);
        if (cancelado()) throw abortar();

        sessao.contentorLocal = contentorLocal;
        await reproduzirQuandoExistir(sessao.video, contentorLocal, cancelado);
      }

      return cliente;
    };

    // TEMPO LIMITE: se a sequência toda não fechar a tempo, desistimos e
    // damos ao ecrã um erro concreto em vez de o deixar a girar para sempre.
    let temporizador = null;
    const comLimite = new Promise((_, rejeitar) => {
      temporizador = setTimeout(() => {
        const e = new Error('Tempo limite a entrar no estádio');
        e.codigoSessao = ERRO.TIMEOUT;
        rejeitar(e);
      }, tempoLimiteMs);
    });

    try {
      const cliente = await Promise.race([sequencia(), comLimite]);
      return { ok: true, cliente };
    } catch (erro) {
      // Seja timeout, permissão negada ou unmount a meio, o estado fica limpo:
      // sem isto o cliente meio-ligado e as pistas já criadas ficavam vivos.
      // Só o Agora: o ecrã continua montado à espera do botão "TENTAR DE NOVO".
      await desligarAgora();
      if (erro?.abortado) return { ok: false, abortado: true };
      const codigo = classificarErro(erro);
      console.error(`[${etiqueta}] não foi possível entrar (${codigo}):`, erro);
      return { ok: false, codigo, erro };
    } finally {
      clearTimeout(temporizador);
      sessao.aEntrar = false;
    }
  };

  sessao.entrar = (opcoes) => {
    // TRANCA ANTI-DUPLO-ARRANQUE (o "isJoining"). Tem de ser síncrona: a fila
    // impede que duas entradas corram ao mesmo tempo, mas não impede que duas
    // sejam PEDIDAS no mesmo tick — e aí a segunda derrubava a primeira.
    // O terminar() limpa esta tranca, por isso o ciclo montar/desmontar/montar
    // do StrictMode continua a conseguir entrar na segunda montagem.
    if (pedidoEmCurso) {
      console.warn(`[${etiqueta}] entrada ignorada: já existe uma a decorrer`);
      return Promise.resolve({ ok: false, ocupado: true });
    }
    pedidoEmCurso = true;
    return encadear(() => entrarInterno(opcoes)).then(
      (resultado) => { pedidoEmCurso = false; return resultado; },
      (erro) => { pedidoEmCurso = false; throw erro; },
    );
  };

  // Estado legível para os testes de verificação (Task 5).
  sessao.diagnostico = () => ({
    etiqueta,
    ligado: !!sessao.cliente,
    estadoLigacao: sessao.cliente?.connectionState || 'DISCONNECTED',
    pistasVivas: [sessao.audio, sessao.video].filter(Boolean).length,
    listenersFirebase: sessao.unsubs.length,
    aEntrar: sessao.aEntrar,
  });

  return sessao;
}
